/**
 * Cookie consent (`tt_consent`), the pure half: the stored record, the
 * cookie string, Google Consent Mode state and the no-recording rule. Shared
 * by the root loader (which reads the cookie to render the right Consent Mode
 * defaults and to know whether to show the banner) and consent.client.ts
 * (which writes it and loads or stops the analytics vendors). Ported from the
 * client's reference script `tt-consent-js` 1.0.0 of 2026-10-02.
 */

/** Bump when the Cookie Policy changes materially. Everyone is asked again. */
export const CONSENT_POLICY_VERSION = '2026-10-02'
export const CONSENT_COOKIE = 'tt_consent'
const CONSENT_MAX_AGE_DAYS = 365
const DAY_MS = 86_400_000
const ROOT_DOMAIN = 'truetenant.ai'

export type ConsentMethod = 'accept_all' | 'custom' | 'decline'

export interface ConsentChoice {
	preferences: boolean
	analytics: boolean
}

export interface ConsentRecord {
	version: string
	/** ISO time of the choice. */
	timestamp: string
	categories: { necessary: true } & ConsentChoice
	method: ConsentMethod
	/** Random id, kept across later changes of mind. */
	id: string
}

/** Vendors that load only after analytics consent (the IDs root.tsx used). */
export const ANALYTICS_VENDORS = {
	clarityProjectId: 'us1zrw5y4s',
	ahrefsKey: 'eW1Sk+eIuYlg2lmvIv6VZA',
} as const

/**
 * Our-domain cookies and storage keys removed when analytics is off.
 * Third-party ones (MUID, MR, SRM_B, ANONCHK on clarity.ms / bing.com) can't
 * be deleted from our pages and expire on their own.
 */
export const ANALYTICS_COOKIES: readonly (string | RegExp)[] = [
	'_ga',
	/^_ga_/,
	'_gid',
	'_gat',
	/^_gat_/,
	'_clck',
	'_clsk',
	'CLID',
	'SM',
	'_cltk',
]
export const ANALYTICS_STORAGE_KEYS: readonly (string | RegExp)[] = [
	'sentryReplaySession',
	'_cltk',
	/^_clsk/,
	/^_clck/,
]
/** The client-hint cookies (app/utils/client-hints.tsx) are Functional. */
export const PREFERENCE_COOKIES: readonly (string | RegExp)[] = [
	'CH-prefers-color-scheme',
	'CH-time-zone',
]

/**
 * No session recording (Clarity, Sentry Replay) on these paths, nor while an
 * element with `data-tt-no-record` is on the page (the screening wizard's
 * identity and bank steps, which share the /tenant/screening path).
 */
const NO_RECORD_PATHS = [
	/\/id-verification/i,
	/\/verify-identity/i,
	/\/veriff/i,
	/\/verify-income/i,
	/\/flinks/i,
	/\/bank/i,
	/\/payment/i,
	/\/checkout/i,
	/\/billing/i,
]
export const NO_RECORD_SELECTOR = '[data-tt-no-record]'

export function isNoRecordPath(pathname: string) {
	return NO_RECORD_PATHS.some((re) => re.test(pathname))
}

function readCookieValue(cookieHeader: string | null, name: string) {
	if (!cookieHeader) return null
	for (const part of cookieHeader.split(/;\s*/)) {
		const eq = part.indexOf('=')
		if (eq > 0 && part.slice(0, eq) === name) return part.slice(eq + 1)
	}
	return null
}

/**
 * The saved record, or null when there is none, it is unreadable, it was
 * made under another policy version, or it is older than 12 months: every
 * null means "show the banner".
 */
export function parseConsentRecord(
	raw: string | null,
	now: number = Date.now(),
): ConsentRecord | null {
	if (!raw) return null
	try {
		const r = JSON.parse(decodeURIComponent(raw)) as Partial<ConsentRecord>
		if (!r || typeof r !== 'object' || !r.categories) return null
		if (r.version !== CONSENT_POLICY_VERSION) return null
		const age = now - new Date(r.timestamp ?? '').getTime()
		if (!(age >= 0) || age > CONSENT_MAX_AGE_DAYS * DAY_MS) return null
		return {
			version: r.version,
			timestamp: r.timestamp as string,
			categories: {
				necessary: true,
				preferences: r.categories.preferences === true,
				analytics: r.categories.analytics === true,
			},
			method:
				r.method === 'accept_all' || r.method === 'decline'
					? r.method
					: 'custom',
			id: typeof r.id === 'string' ? r.id : '',
		}
	} catch {
		return null
	}
}

export function getConsentFromCookieHeader(
	cookieHeader: string | null,
	now: number = Date.now(),
) {
	return parseConsentRecord(readCookieValue(cookieHeader, CONSENT_COOKIE), now)
}

export function choiceFor(
	method: ConsentMethod,
	custom?: ConsentChoice,
): ConsentChoice {
	if (method === 'accept_all') return { preferences: true, analytics: true }
	if (method === 'decline') return { preferences: false, analytics: false }
	return {
		preferences: custom?.preferences === true,
		analytics: custom?.analytics === true,
	}
}

export function makeConsentRecord({
	method,
	choice,
	previous,
	now,
	newId,
}: {
	method: ConsentMethod
	choice: ConsentChoice
	previous: ConsentRecord | null
	now: Date
	newId: string
}): ConsentRecord {
	return {
		version: CONSENT_POLICY_VERSION,
		timestamp: now.toISOString(),
		categories: {
			necessary: true,
			preferences: choice.preferences,
			analytics: choice.analytics,
		},
		method,
		id: previous?.id || newId,
	}
}

/**
 * ".truetenant.ai" on truetenant.ai and every subdomain, so one choice covers
 * the site and the app; host-only elsewhere (localhost, previews).
 */
export function consentCookieDomain(hostname: string) {
	if (hostname === ROOT_DOMAIN || hostname.endsWith(`.${ROOT_DOMAIN}`)) {
		return `.${ROOT_DOMAIN}`
	}
	return null
}

export function serializeConsentCookie(
	record: ConsentRecord,
	{ hostname, secure }: { hostname: string; secure: boolean },
) {
	const value = encodeURIComponent(JSON.stringify(record))
	const parts = [
		`${CONSENT_COOKIE}=${value}`,
		`Max-Age=${CONSENT_MAX_AGE_DAYS * 86_400}`,
		'Path=/',
		'SameSite=Lax',
	]
	const domain = consentCookieDomain(hostname)
	if (domain) parts.push(`Domain=${domain}`)
	if (secure) parts.push('Secure')
	return parts.join('; ')
}

export function matchesAny(
	name: string,
	patterns: readonly (string | RegExp)[],
) {
	return patterns.some((p) =>
		typeof p === 'string' ? p === name : p.test(name),
	)
}

/**
 * Every `document.cookie` assignment that expires `name` on the host and on
 * each parent domain, so copies set on ".truetenant.ai" and on
 * "app.truetenant.ai" both go.
 */
export function expireCookieStrings(name: string, hostname: string) {
	const labels = hostname.split('.')
	const domains: (string | null)[] = [null]
	for (let i = 0; i < labels.length - 1; i++) {
		const d = labels.slice(i).join('.')
		domains.push(d, `.${d}`)
	}
	return domains.map(
		(d) => `${name}=; Max-Age=0; Path=/${d ? `; Domain=${d}` : ''}`,
	)
}

/** Google Consent Mode v2 for a record. Ad signals stay denied: no ads. */
export function consentModeState(choice: ConsentChoice | null) {
	return {
		analytics_storage: choice?.analytics ? 'granted' : 'denied',
		functionality_storage: choice?.preferences ? 'granted' : 'denied',
		personalization_storage: 'denied',
		ad_storage: 'denied',
		ad_user_data: 'denied',
		ad_personalization: 'denied',
	} as const
}

/**
 * The inline head script that must run before Google Tag Manager and gtag.js:
 * everything denied by default, then the saved record's choices for a
 * returning visitor, so their tags never fire unconsented.
 */
export function consentModeBootScript(record: ConsentRecord | null) {
	const lines = [
		'window.dataLayer=window.dataLayer||[];',
		'function gtag(){dataLayer.push(arguments)}',
		"if(typeof window.gtag!=='function')window.gtag=gtag;",
		`gtag('consent','default',${JSON.stringify({
			...consentModeState(null),
			security_storage: 'granted',
			wait_for_update: 500,
		})});`,
		"gtag('set','ads_data_redaction',true);",
		"gtag('set','url_passthrough',false);",
	]
	if (record) {
		lines.push(
			`gtag('consent','update',${JSON.stringify(consentModeState(record.categories))});`,
		)
	}
	return lines.join('\n')
}
